import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { categoryColor } from '../Vendors/VendorFourthParties';

/**
 * FluxWatch report format. Three PDF documents share one engine so they look like one family:
 *   - Security report (detailed, one vendor)
 *   - Vendor summary report (4 pages, one vendor)
 *   - Portfolio risk report (every vendor)
 * Light, card-based layout: a pale header band with the logo on every page, grade badges, rating bars and
 * history charts. Every number comes from stored scans (routes/report_routes.py); nothing here scores anything.
 */

/* ─── Data shapes (GET /reports/vendor/{id}, GET /reports/portfolio) ─────────────────────────────── */
export interface ReportFinding {
  name: string; category: string; severity: string; score_impact: number;
  description: string; recommendation: string; environment?: string | null;
  endpoints?: string[]; endpoint_total?: number;
}
export interface VendorReportData {
  issued_by?: string | null;
  vendor: {
    id: string; name: string; domain: string; is_self?: boolean; category?: string | null; business_units?: string[];
    location?: string | null; parent_company?: string | null; lifecycle?: string | null; data_sensitivity?: string | null;
    vendor_since?: string | null; contract_end_date?: string | null; owner_name?: string | null; business_owner?: string | null;
  };
  scan: null | {
    id: string; completed_at: string; score: number | null; risk_level: string | null; scan_type?: string | null;
    category_scores: Record<string, number>; counts: Record<string, number>; indicator_total: number;
    assets: string[]; asset_total: number;
  };
  findings: ReportFinding[];
  passing: { name: string; category: string; status?: 'passed' | 'not_tested'; description?: string; recommendation?: string }[];
  history: { date: string; score: number; category_scores?: Record<string, number> }[];
  fourth_parties: {
    company: string; product: string; category: string; domain: string;
    description?: string | null; source?: string | null; detected_by?: string | null; first_detected?: string | null;
  }[];
  /** Company domain -> embeddable logo (data URI), fetched by the backend; missing = draw a letter tile. */
  fourth_party_logos?: Record<string, string>;
  remediation: {
    title: string; status: string; updated_at: string | null; due_date: string | null; risk_count: number; open_risk_count: number;
    risks: { name: string; category: string; severity: string; status: string }[];
  }[];
  geolocation: { country: string; code?: string | null; ips: number; owners: string[] }[];
  ip_total: number;
  hosts: { host: string; active: boolean; ip?: string | null; environment?: string | null }[];
  categories: string[];
}
export interface PortfolioVendor {
  id: string; name: string; domain: string; category?: string | null; lifecycle?: string | null;
  data_sensitivity?: string | null; contract_end_date?: string | null; owner_name?: string | null;
  score: number | null; risk_level: string | null; previous_score: number | null; scan_date: string | null;
  category_scores: Record<string, number>; counts: Record<string, number>;
  top_findings: { name: string; category: string; severity: string; score_impact: number }[];
}
export interface PortfolioReportData { company_name: string | null; vendors: PortfolioVendor[]; categories: string[] }

export type Classification = 'Confidential' | 'Internal' | 'Restricted';
export interface ReportLogo { data: string; w: number; h: number }
export interface ReportMeta {
  reportId: string; generatedAt: Date; preparedFor: string; preparedBy: string; classification: Classification;
  logo?: ReportLogo | null;
}
export interface VendorReportOptions {
  variant: 'detailed' | 'summary';
  introduction: boolean; profile: boolean; remediation: boolean; categories: boolean; emptyCategories: boolean;
  geolocation: boolean; fourthParties: boolean; evidenceUsed: boolean;
}
export interface PortfolioReportOptions { heatmap: boolean; topRisks: boolean; calendar: boolean; methodology: boolean }

export const DEFAULT_VENDOR_OPTIONS: VendorReportOptions = {
  variant: 'detailed', introduction: true, profile: true, remediation: true, categories: true, emptyCategories: true,
  geolocation: true, fourthParties: true, evidenceUsed: true,
};
export const DEFAULT_PORTFOLIO_OPTIONS: PortfolioReportOptions = { heatmap: true, topRisks: true, calendar: true, methodology: true };

/* ─── Scales ────────────────────────────────────────────────────────────────────────────────────── */
type RGB = [number, number, number];
const P = {
  ink: [22, 28, 45] as RGB, body: [70, 80, 100] as RGB, muted: [112, 122, 142] as RGB, faint: [160, 168, 184] as RGB,
  line: [226, 231, 238] as RGB, band: [242, 246, 251] as RGB, white: [255, 255, 255] as RGB,
  brand: [59, 79, 191] as RGB, grid: [235, 238, 243] as RGB, chart: [88, 120, 220] as RGB,
};
export const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'] as const;
const SEV: Record<string, RGB> = {
  Critical: [214, 40, 40], High: [234, 100, 22], Medium: [240, 160, 20], Low: [226, 190, 30], Info: [112, 122, 142],
  Passed: [22, 163, 74], 'Not tested': [160, 168, 184],
};
export const PRIORITY: Record<string, { label: string; window: string }> = {
  Critical: { label: 'Immediate', window: 'within 7 days' },
  High: { label: 'Short term', window: 'within 30 days' },
  Medium: { label: 'Medium term', window: 'within 90 days' },
  Low: { label: 'Planned', window: 'next review cycle' },
};
const SEVERITY_MEANING: Record<string, string> = {
  Critical: 'Exploitable exposure that is likely to lead to compromise or serious harm.',
  High: 'A significant weakness that materially increases the chance of an incident.',
  Medium: 'A weakness that adds to risk, especially in combination with others.',
  Low: 'A hygiene or best-practice gap with limited direct impact.',
};
export const gradeOf = (s: number | null | undefined) =>
  s == null ? '-' : s >= 90 ? 'A' : s >= 80 ? 'B' : s >= 70 ? 'C' : s >= 60 ? 'D' : 'F';
const GRADE: Record<string, RGB> = {
  A: [34, 167, 94], B: [132, 190, 52], C: [238, 184, 20], D: [244, 128, 32], F: [222, 52, 52], '-': [176, 184, 198],
};
const gradeColor = (s: number | null | undefined) => GRADE[gradeOf(s)];
const HEAT: Record<string, { fg: RGB; bg: RGB }> = {
  A: { fg: [21, 128, 61], bg: [220, 247, 230] }, B: { fg: [77, 124, 15], bg: [236, 250, 210] },
  C: { fg: [146, 100, 10], bg: [254, 243, 199] }, D: { fg: [168, 70, 12], bg: [255, 231, 209] },
  F: { fg: [170, 30, 30], bg: [254, 222, 222] }, '-': { fg: [160, 168, 184], bg: [246, 248, 251] },
};
export const riskLabel = (level?: string | null) => {
  const l = (level || '').toLowerCase();
  return l ? `${l[0].toUpperCase()}${l.slice(1)} risk` : 'Not assessed';
};
const RATING_SENTENCE: Record<string, string> = {
  A: 'has strong security controls in place, with few or no gaps in its security posture.',
  B: 'has reasonable security controls in place but could have gaps in its security posture.',
  C: 'has some security controls in place, but there are notable gaps in its security posture.',
  D: 'has significant gaps in its security controls that increase the likelihood of an incident.',
  F: 'has serious weaknesses in its security posture that need urgent attention.',
  '-': 'has not been assessed yet.',
};
const SENSITIVITY: Record<string, string> = {
  none: 'No sensitive data', internal: 'Internal data', pii: 'Personal data (PII)', financial: 'Financial data', regulated: 'Regulated data',
};
const LIFECYCLE: Record<string, string> = {
  due_diligence: 'Due diligence', active_monitoring: 'Active monitoring', offboarding: 'Offboarding',
};
const SHORT_CAT: Record<string, string> = {
  'Domain & DNS': 'DNS', 'Network Security': 'Network', 'Web Security': 'Web', 'TLS/Encryption': 'TLS',
  'Threat Intelligence': 'Threat', 'Cloud Security': 'Cloud', 'Infrastructure': 'Infra', 'Attack Surface': 'Attack',
  'Cubit Score': 'Cubit', 'Patching Cadence': 'Patching', 'Compliance': 'Compl.', 'Legal & Regulatory': 'Legal',
  'Market Intelligence': 'Market', 'Reputation & ESG': 'ESG',
};
export const CATEGORY_INFO: Record<string, string> = {
  'Domain & DNS': 'DNS and email-domain security covers the records that keep an organisation in control of its domains and stop others from sending mail in its name: SPF, DKIM, DMARC, DNSSEC, CAA and registrar protections. Weak settings let attackers impersonate the organisation or take over its domains.',
  'Network Security': 'Network security looks at the services exposed on the organisation\'s IP addresses. Services meant for internal networks, outdated protocols and unnecessary open ports give attackers direct points of entry and should be closed or placed behind access controls.',
  'Web Security': 'Web security covers the controls that let websites be exposed to the internet safely. Many are server security headers and cookie settings that make sure a site only serves trusted content and protects visitors\' sessions.',
  'TLS/Encryption': 'Encryption of data in transit prevents adversary-in-the-middle attacks. Traffic over the internet should always be encrypted with current protocols, strong keys and valid certificates, and HTTPS should be enforced.',
  'Threat Intelligence': 'Threat intelligence draws on breach, leak, malware and blocklist sources. Findings here can mean credentials or data have already been exposed, or that the organisation\'s infrastructure has been reported for malicious activity.',
  'Cloud Security': 'Cloud security covers publicly visible cloud resources such as storage buckets, hosting configuration and cloud service exposure. Misconfigured cloud assets are a leading cause of data exposure.',
  'Infrastructure': 'Infrastructure covers the resilience and hygiene of the systems that serve the organisation: hosting, routing, redundancy, service status history and supporting services.',
  'Attack Surface': 'Attack surface risks present attackers with additional points to target on the organisation\'s external boundary. Reducing the attack surface means decommissioning unused assets or moving them behind network-level protection.',
  'Cubit Score': 'The Cubit score summarises application security, network hygiene and DNS health signals into a composite view of how well the organisation maintains its internet-facing estate.',
  'Patching Cadence': 'Patching cadence measures how quickly known vulnerabilities are fixed. Software with known or actively exploited vulnerabilities, and end-of-life software that no longer receives updates, should be upgraded on a timeline that matches its severity.',
  'Compliance': 'Compliance looks for publicly verifiable evidence of security certifications and programmes such as ISO 27001 and SOC 2, and for the policies an organisation publishes about how it handles data.',
  'Legal & Regulatory': 'Legal and regulatory risk covers litigation, regulatory investigations, sanctions screening and mandatory cyber-incident disclosures that indicate exposure beyond technical controls.',
  'Market Intelligence': 'Market intelligence reflects business signals such as financial stability, news and market reputation that can affect a supplier\'s ability to keep operating and investing in security.',
  'Reputation & ESG': 'Reputation and ESG covers brand impersonation, public sentiment and environmental, social and governance signals that indicate reputational exposure.',
};

/** jsPDF's built-in fonts only cover Latin-1; scan text carries emoji and mis-decoded UTF-8. */
export const clean = (value: unknown): string => {
  if (value == null) return '';
  return String(value)
    .replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, '-').replace(/…/g, '...')
    .replace(/[•‣◦⁃∙]/g, '-').replace(/−/g, '-')
    .replace(/[^\x20-\x7E\n\t]/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n').map(l => l.trim()).join('\n')
    .trim();
};
export const fmtDate = (d?: string | Date | null, long = false) => {
  if (!d) return '-';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', long ? { month: 'long', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};
export const daysSince = (d?: string | null) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000) : null);
export const newReportId = (prefix: string) => {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  return `${prefix}-${stamp}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
};

/* ─── Engine ────────────────────────────────────────────────────────────────────────────────────── */
const PW = 210, PH = 297, ML = 16, MR = 16, CW = PW - ML - MR;
const BAND_H = 21, TOP = BAND_H + 11, BOTTOM = PH - 16;

interface TableOpts {
  widths?: Record<number, number>; align?: Record<number, 'left' | 'center' | 'right'>;
  /** Column that gets a severity icon in front of its text; one severity per body row. */
  sevIconCol?: number; sevIcons?: string[];
  /** Column whose numeric value is shown as a grade badge followed by the number (or the badge only). */
  gradeCol?: number; gradeOnly?: boolean;
  bold0?: boolean; size?: number; empty?: string;
  /** Column that gets a company logo (or a letter tile) in front of its text; one entry per body row. */
  logoCol?: number; logos?: { uri: string | null; letter: string; key: string }[];
  /** Column whose text is coloured per body row. */
  colorCol?: number; colors?: RGB[];
}

class Doc {
  doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  y = TOP;
  constructor(public meta: ReportMeta, public title: string, public subtitle: string, public hasCover: boolean) {
    this.doc.setProperties({ title, subject: subtitle, author: meta.preparedBy, creator: 'FluxWatch' });
    this.doc.setLineCap('round');
  }
  get page() { return this.doc.getNumberOfPages(); }
  fill(c: RGB) { this.doc.setFillColor(c[0], c[1], c[2]); }
  stroke(c: RGB) { this.doc.setDrawColor(c[0], c[1], c[2]); }
  color(c: RGB) { this.doc.setTextColor(c[0], c[1], c[2]); }
  font(style: 'normal' | 'bold' | 'italic', size: number) { this.doc.setFont('helvetica', style); this.doc.setFontSize(size); }
  text(s: string | string[], x: number, y: number, o: any = {}) { this.doc.text(Array.isArray(s) ? s.map(clean) : clean(s), x, y, o); }
  wrap(s: string, w: number) { return this.doc.splitTextToSize(clean(s), w) as string[]; }
  width(s: string) { return this.doc.getTextWidth(clean(s)); }
  newPage() { this.doc.addPage(); this.y = TOP; }
  need(h: number) { if (this.y + h > BOTTOM) this.newPage(); }

  logo(x: number, y: number, h: number, alignRight = false) {
    const l = this.meta.logo;
    if (!l) {
      this.font('bold', h * 2.2); this.color(P.ink);
      this.doc.text('FluxWatch', x, y + h * 0.8, alignRight ? { align: 'right' } : {});
      return;
    }
    const w = h * l.w / l.h;
    this.doc.addImage(l.data, 'PNG', alignRight ? x - w : x, y, w, h, 'fluxwatch-logo', 'FAST');
  }

  card(x: number, y: number, w: number, h: number) {
    this.fill(P.white); this.stroke(P.line); this.doc.setLineWidth(0.3);
    this.doc.roundedRect(x, y, w, h, 1.8, 1.8, 'FD');
  }

  label(s: string, x: number, y: number, c: RGB = P.muted, size = 6.2) {
    this.font('bold', size); this.color(c);
    this.doc.text(clean(s).toUpperCase(), x, y, { charSpace: 0.3 });
  }

  /** Page heading: optional small eyebrow above a large title. */
  heading(title: string, eyebrow?: string) {
    this.need(24);
    if (eyebrow) { this.font('normal', 7.8); this.color(P.brand); this.text(eyebrow, ML, this.y); this.y += 6.5; }
    this.font('bold', 16); this.color(P.ink); this.text(title, ML, this.y);
    this.y += 9;
  }

  sub(title: string) {
    this.need(16);
    this.font('bold', 11); this.color(P.ink); this.text(title, ML, this.y);
    this.y += 6;
  }

  paragraph(s: string, o: { size?: number; color?: RGB; x?: number; w?: number; style?: 'normal' | 'bold' | 'italic' } = {}) {
    const size = o.size ?? 8.8, x = o.x ?? ML, w = o.w ?? CW, lh = size * 0.46;
    for (const block of clean(s).split('\n')) {
      if (!block) { this.y += lh * 0.6; continue; }
      this.font(o.style ?? 'normal', size);
      for (const line of this.wrap(block, w)) {
        this.need(lh + 1);
        this.font(o.style ?? 'normal', size); this.color(o.color ?? P.body);
        this.doc.text(line, x, this.y);
        this.y += lh;
      }
    }
    this.y += 2;
  }

  bullets(items: string[], size = 8.8) {
    const lh = size * 0.46;
    items.forEach(item => {
      this.font('normal', size);
      const lines = this.wrap(item, CW - 6);
      this.need(lines.length * lh + 2);
      this.fill(P.brand); this.doc.circle(ML + 1.2, this.y - 1.1, 0.65, 'F');
      this.font('normal', size); this.color(P.body); this.doc.text(lines, ML + 5, this.y);
      this.y += lines.length * lh + 1.6;
    });
    this.y += 1;
  }

  /** Grade badge: outlined circle with the grade letter. */
  grade(cx: number, cy: number, score: number | null | undefined, r = 2.4) {
    const c = gradeColor(score);
    this.fill(P.white); this.stroke(c); this.doc.setLineWidth(r * 0.17);
    this.doc.circle(cx, cy, r, 'FD');
    this.font('bold', r * 3.1); this.color(c);
    this.doc.text(gradeOf(score), cx, cy + r * 0.38, { align: 'center' });
  }

  /** Severity icon: filled circle with a white "!" (critical/high), bar (medium) or dot (low). */
  sevIcon(cx: number, cy: number, sev: string, r = 1.9) {
    this.fill(SEV[sev] || SEV.Info); this.doc.circle(cx, cy, r, 'F');
    this.fill(P.white);
    if (sev === 'Passed') {
      this.stroke(P.white); this.doc.setLineWidth(r * 0.24);
      this.doc.line(cx - r * 0.45, cy + r * 0.02, cx - r * 0.12, cy + r * 0.36);
      this.doc.line(cx - r * 0.12, cy + r * 0.36, cx + r * 0.48, cy - r * 0.32);
    } else if (sev === 'Not tested') {
      this.doc.rect(cx - r * 0.45, cy - r * 0.11, r * 0.9, r * 0.22, 'F');
    } else if (sev === 'Critical' || sev === 'High') {
      this.doc.rect(cx - r * 0.13, cy - r * 0.58, r * 0.26, r * 0.66, 'F');
      this.doc.circle(cx, cy + r * 0.42, r * 0.15, 'F');
    } else if (sev === 'Medium') {
      this.doc.rect(cx - r * 0.5, cy - r * 0.13, r, r * 0.26, 'F');
    } else {
      this.doc.circle(cx, cy, r * 0.32, 'F');
    }
  }

  /** A company's logo, or (no logo available) its first letter on a pale rounded tile -- the app's own fallback. */
  companyLogo(x: number, y: number, size: number, logo: { uri: string | null; letter: string; key: string }) {
    if (logo.uri) {
      try {
        const fmt = logo.uri.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
        this.doc.addImage(logo.uri, fmt, x, y, size, size, `logo-${logo.key}`, 'FAST');
        return;
      } catch { /* unreadable image: fall through to the letter tile */ }
    }
    this.fill(P.band); this.stroke(P.line); this.doc.setLineWidth(0.2);
    this.doc.roundedRect(x, y, size, size, 1.1, 1.1, 'FD');
    this.font('bold', size * 1.9); this.color(P.muted);
    this.doc.text(logo.letter, x + size / 2, y + size * 0.72, { align: 'center' });
  }

  /** Big "score / 100" with its badge. Returns nothing; draws at (x, baseline y). */
  bigScore(x: number, y: number, score: number | null, size = 20) {
    const r = size * 0.16;
    this.grade(x + r, y - size * 0.13, score, r);
    this.font('bold', size); this.color(gradeColor(score));
    const s = score == null ? '--' : String(Math.round(score));
    this.text(s, x + 2 * r + 2, y);
    const sw = this.width(s);
    this.font('normal', size * 0.42); this.color(P.faint); this.text('/ 100', x + 2 * r + 3.2 + sw, y);
  }

  /** "OVERALL SECURITY RATING" card. */
  ratingCard(score: number | null, subject: string, label = 'Overall security rating') {
    this.font('normal', 8);
    const sentence = this.wrap(`${subject} ${RATING_SENTENCE[gradeOf(score)]}`, CW - 10);
    const h = 21 + sentence.length * 3.8;
    this.need(h + 6);
    this.card(ML, this.y, CW, h);
    this.label(label, ML + 5, this.y + 6.5);
    this.bigScore(ML + 5, this.y + 16, score);
    this.font('normal', 8); this.color(P.body); this.doc.text(sentence, ML + 5, this.y + 22.5);
    this.y += h + 8;
  }

  /** Rating bars card: label, grade badge, score and a bar per row. */
  ratingBars(rows: { cat: string; score: number | null }[], title: string, note: string) {
    const rowH = 6.3, h = 20 + rows.length * rowH;
    this.need(h + 12);
    this.sub(title);
    const y0 = this.y;
    this.card(ML, y0, CW, h);
    this.font('normal', 7); this.color(P.brand); this.text(note, ML + 5, y0 + 7);
    const bx = ML + 50, bw = CW - 50 - 6, axisY = y0 + 13;
    this.font('normal', 5.6); this.color(P.faint);
    for (let t = 0; t <= 100; t += 10) {
      const x = bx + bw * t / 100;
      this.doc.text(String(t), x, axisY, { align: 'center' });
      this.stroke(P.grid); this.doc.setLineWidth(0.15); this.doc.line(x, axisY + 1.5, x, y0 + h - 4);
    }
    rows.forEach((r, i) => {
      const cy = axisY + 6 + i * rowH;
      this.font('normal', 6.8); this.color(P.ink);
      const lines = this.wrap(r.cat, 25).slice(0, 2);
      this.doc.text(lines, ML + 5, cy + (lines.length > 1 ? -0.4 : 1));
      this.grade(ML + 33, cy, r.score, 1.9);
      this.font('normal', 6.8); this.color(P.body); this.text(r.score == null ? '-' : String(Math.round(r.score)), ML + 36.5, cy + 1);
      if (r.score != null && r.score > 0) {
        this.fill(gradeColor(r.score)); this.doc.rect(bx, cy - 2.2, Math.max(0.8, bw * Math.min(r.score, 100) / 100), 4.4, 'F');
      }
    });
    this.y = y0 + h + 8;
  }

  /** Line chart of the last 12 months of a rating, drawn in the box (x0, top, w, h). */
  chart(points: { date: string; score: number }[], x0: number, top: number, w: number, h: number,
    extra: { points: { date: string; score: number }[]; color: RGB }[] = []) {
    const yOf = (s: number) => top + h - (s / 100) * h;
    this.font('normal', 5.8); this.color(P.faint);
    for (let t = 0; t <= 100; t += 20) {
      this.stroke(P.grid); this.doc.setLineWidth(0.15); this.doc.line(x0, yOf(t), x0 + w, yOf(t));
      this.doc.text(String(t), x0 - 3, yOf(t) + 1, { align: 'right' });
    }
    const end = new Date(), start = new Date(end.getTime() - 365 * 86_400_000);
    const xOf = (d: Date) => x0 + w * Math.min(1, Math.max(0, (d.getTime() - start.getTime()) / (end.getTime() - start.getTime())));
    for (let m = 1; m <= 12; m += 2) {
      const d = new Date(start.getFullYear(), start.getMonth() + m, 1);
      if (d > end) continue;
      this.font('normal', 5.8); this.color(P.faint);
      this.doc.text(`${d.toLocaleDateString('en-US', { month: 'short' })} '${String(d.getFullYear()).slice(2)}`, xOf(d), top + h + 5, { align: 'center' });
    }
    // Comparison series (e.g. the vendor average): dashed, no markers, drawn under the main line.
    extra.forEach(series => {
      const ps = series.points.filter(p => typeof p.score === 'number').map(p => ({ x: xOf(new Date(p.date)), y: yOf(p.score) }));
      if (ps.length < 2) return;
      this.stroke(series.color); this.doc.setLineWidth(0.5); this.doc.setLineDashPattern([1.2, 1], 0);
      for (let i = 1; i < ps.length; i++) this.doc.line(ps[i - 1].x, ps[i - 1].y, ps[i].x, ps[i].y);
      this.doc.setLineDashPattern([], 0);
    });
    const pts = points.filter(p => typeof p.score === 'number' && !Number.isNaN(new Date(p.date).getTime()))
      .map(p => ({ x: xOf(new Date(p.date)), y: yOf(p.score) }));
    if (!pts.length) {
      this.font('normal', 7.5); this.color(P.muted); this.text('No rating history yet.', x0 + w / 2, top + h / 2, { align: 'center' });
      return;
    }
    // Carry the last rating forward to today, so a stable rating reads as a flat line.
    const line = [...pts, { x: x0 + w, y: pts[pts.length - 1].y }];
    this.stroke(P.chart); this.doc.setLineWidth(0.55);
    for (let i = 1; i < line.length; i++) this.doc.line(line[i - 1].x, line[i - 1].y, line[i].x, line[i].y);
    pts.forEach(p => { this.fill(P.white); this.stroke(P.chart); this.doc.setLineWidth(0.4); this.doc.circle(p.x, p.y, 0.7, 'FD'); });
  }

  historyCard(points: { date: string; score: number }[], title: string, label: string) {
    const height = 60;
    this.need(height + 14);
    this.sub(title);
    const y0 = this.y;
    this.card(ML, y0, CW, height);
    this.font('normal', 7); this.color(P.brand); this.text(label, ML + 5, y0 + 7);
    this.chart(points, ML + 14, y0 + 13, CW - 22, height - 25);
    this.y = y0 + height + 8;
  }

  /** Small stat tiles: label above a big number. */
  stats(items: { label: string; value: string }[]) {
    this.need(25);
    const gap = 4, w = (CW - gap * (items.length - 1)) / items.length;
    items.forEach((k, i) => {
      const x = ML + i * (w + gap);
      this.card(x, this.y, w, 18);
      this.label(k.label, x + 4, this.y + 6);
      this.font('bold', 15); this.color(P.ink); this.text(k.value, x + 4, this.y + 14);
    });
    this.y += 25;
  }

  table(head: string[], body: (string | number)[][], o: TableOpts = {}) {
    if (!body.length && o.empty) { this.paragraph(o.empty, { color: P.muted }); this.y += 2; return; }
    const pad = { top: 2.6, bottom: 2.6, left: 2, right: 2 };
    const columnStyles: Record<number, any> = {};
    const col = (i: number, extra: any) => { columnStyles[i] = { ...(columnStyles[i] || {}), ...extra }; };
    Object.entries(o.widths || {}).forEach(([k, v]) => col(+k, { cellWidth: v }));
    Object.entries(o.align || {}).forEach(([k, v]) => col(+k, { halign: v }));
    if (o.bold0) col(0, { fontStyle: 'bold', textColor: P.ink });
    if (o.sevIconCol != null) col(o.sevIconCol, { cellPadding: { ...pad, left: 7.2 } });
    if (o.gradeCol != null) col(o.gradeCol, { cellPadding: { ...pad, left: 7.4 } });
    if (o.logoCol != null) col(o.logoCol, { cellPadding: { ...pad, left: 9 }, minCellHeight: 9 });
    autoTable(this.doc, {
      startY: this.y, head: [head.map(clean)], body: body.map(r => r.map(c => clean(c))),
      margin: { left: ML, right: MR, top: TOP, bottom: PH - BOTTOM },
      // A logo row split over a page break loses its logo on the second half; keep those rows whole.
      rowPageBreak: o.logoCol != null ? 'avoid' : 'auto',
      theme: 'plain',
      styles: { font: 'helvetica', fontSize: o.size ?? 7.6, textColor: P.body, cellPadding: pad, overflow: 'linebreak', valign: 'top', lineColor: P.line },
      headStyles: { fontSize: 6.4, fontStyle: 'normal', textColor: P.muted, valign: 'bottom' },
      columnStyles,
      didParseCell: (d: any) => {
        d.cell.styles.lineWidth = { bottom: d.section === 'head' ? 0.35 : 0.2 };
        d.cell.styles.lineColor = d.section === 'head' ? P.ink : P.line;
        if (d.section === 'head') {
          d.cell.styles.cellPadding = { top: 1.5, bottom: 1.8, left: 2, right: 2 };
          const al = o.align?.[d.column.index];
          if (al) d.cell.styles.halign = al;
        }
        if (d.section === 'body' && o.gradeOnly && d.column.index === o.gradeCol) d.cell.text = [''];
        if (d.section === 'body' && d.column.index === o.colorCol && o.colors?.[d.row.index]) {
          d.cell.styles.textColor = o.colors[d.row.index]; d.cell.styles.fontStyle = 'bold';
        }
      },
      didDrawCell: (d: any) => {
        if (d.section !== 'body') return;
        const cy = d.cell.y + 2.6 + 1.1;
        if (d.column.index === o.sevIconCol && o.sevIcons) this.sevIcon(d.cell.x + 3.6, cy, o.sevIcons[d.row.index], 1.7);
        if (d.column.index === o.logoCol && o.logos?.[d.row.index]) this.companyLogo(d.cell.x + 1.6, d.cell.y + 2, 5.4, o.logos[d.row.index]);
        if (d.column.index === o.gradeCol) {
          const n = Number(d.cell.raw);
          this.grade(d.cell.x + 3.8, cy, d.cell.raw !== '-' && d.cell.raw !== '' && Number.isFinite(n) ? n : null, 1.9);
        }
      },
    });
    this.y = (this.doc as any).lastAutoTable.finalY + 8;
  }

  /** Header band with title + logo on every page after the cover, and the footer. */
  finish() {
    const total = this.page;
    for (let p = this.hasCover ? 2 : 1; p <= total; p++) {
      this.doc.setPage(p);
      this.fill(P.band); this.doc.rect(0, 0, PW, BAND_H, 'F');
      this.font('bold', 11); this.color(P.ink); this.text(this.title, ML, 10);
      this.font('normal', 7.6); this.color(P.brand); this.text(this.subtitle, ML, 15);
      this.logo(PW - MR, 6.4, 7.6, true);
      this.font('normal', 6.8); this.color(P.brand);
      this.text(`${this.title}  |  ${this.meta.classification}  |  ${this.meta.reportId}`, ML, PH - 8);
      this.color(P.muted); this.doc.text(`Page ${p}/${total}`, PW - MR, PH - 8, { align: 'right' });
    }
  }

  /** Light cover: pale panel with the title, white strip with report facts. */
  cover(title: string[], intro: string, facts: [string, string][]) {
    const d = this.doc;
    this.fill(P.band); d.rect(0, 0, PW, 236, 'F');
    this.logo(ML, 16, 9.5);
    this.font('normal', 6.8); this.color(P.muted);
    d.text(this.meta.classification.toUpperCase(), PW - MR, 22, { align: 'right', charSpace: 0.4 });
    this.font('normal', 27); this.color(P.ink);
    let y = 92;
    title.forEach(line => { this.wrap(line, CW).forEach(l => { d.text(l, ML, y); y += 11.5; }); });
    this.font('normal', 10); this.color(P.body);
    d.text(this.wrap(intro, CW * 0.85), ML, y + 4);
    const colW = CW / facts.length;
    facts.forEach(([k, v], i) => {
      const x = ML + i * colW;
      this.font('normal', 6.8); this.color(P.muted); this.text(k, x, 254);
      this.font('normal', 9); this.color(P.ink); this.text(this.wrap(v || '-', colW - 4)[0] || '-', x, 260.5);
    });
    this.font('normal', 6.8); this.color(P.muted);
    this.text(`This report has been generated using FluxWatch for ${this.meta.preparedFor}. It is ${this.meta.classification.toLowerCase()} and intended only for its recipients.`, ML, 276);
  }
}

/* ─── Vendor sections ───────────────────────────────────────────────────────────────────────────── */
function categoryCounts(data: VendorReportData) {
  const counts: Record<string, Record<string, number>> = {};
  data.categories.forEach(c => { counts[c] = { Critical: 0, High: 0, Medium: 0, Low: 0 }; });
  data.findings.forEach(f => { if (counts[f.category] && counts[f.category][f.severity] !== undefined) counts[f.category][f.severity]++; });
  return counts;
}

function assessmentSummary(p: Doc, data: VendorReportData, subject: string, withHistory: boolean) {
  const s = data.scan!;
  p.heading('Assessment summary');
  p.ratingCard(s.score, subject);
  const rows = data.categories.map(cat => ({ cat, score: s.category_scores?.[cat] ?? null }))
    .sort((a, b) => (a.score ?? 101) - (b.score ?? 101));
  p.ratingBars(rows, 'Security rating by category', 'Lowest rating first');
  if (withHistory) p.historyCard(data.history, 'Security rating (last 12 months)', 'Vendor rating');
}

function riskCountTable(p: Doc, data: VendorReportData) {
  const counts = categoryCounts(data);
  p.heading('Risk count by severity');
  p.table(['Category', 'Category rating', 'Critical', 'High', 'Medium', 'Low'],
    data.categories.map(cat => {
      const c = counts[cat], sc = data.scan?.category_scores?.[cat];
      return [cat, sc == null ? '-' : String(Math.round(sc)), c.Critical || '-', c.High || '-', c.Medium || '-', c.Low || '-'];
    }),
    { widths: { 0: 58, 1: 32, 2: 22, 3: 22, 4: 22, 5: 22 }, align: { 2: 'center', 3: 'center', 4: 'center', 5: 'center' }, gradeCol: 1, size: 8 });
}

function remediationSummary(p: Doc, data: VendorReportData) {
  p.heading('Remediation summary');
  p.sub('Active remediation requests');
  const reqs = data.remediation || [];
  const STATUS: Record<string, string> = { in_progress: 'In progress', awaiting_review: 'Awaiting review', completed: 'Completed' };
  p.table(['Title', 'Risks open', 'Risks', 'Due', 'Last updated', 'Status'],
    reqs.map(r => [r.title, String(r.open_risk_count ?? 0), String(r.risk_count ?? 0), r.due_date ? fmtDate(r.due_date) : '-', fmtDate(r.updated_at), STATUS[r.status] || r.status]),
    { widths: { 1: 18, 2: 15, 3: 24, 4: 26, 5: 26 }, align: { 1: 'center', 2: 'center' }, bold0: true, empty: 'There are no active remediation requests for this vendor.' });
  const risks = reqs.flatMap(r => r.risks || []);
  p.sub('Risks in remediation');
  p.table(['Finding', 'Category', 'Status'],
    risks.map(k => [k.name, k.category || '-', k.status === 'fixed' ? 'Remediated' : 'Not remediated']),
    { widths: { 1: 46, 2: 32 }, sevIconCol: 0, sevIcons: risks.map(k => k.severity), empty: 'No risks are currently in remediation.' });
}

const hexRgb = (hex: string): RGB => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Companies and products the vendor itself depends on (the vendor page's Fourth Parties tab), one row per company. */
function fourthPartiesSection(p: Doc, data: VendorReportData) {
  const rows = data.fourth_parties || [];
  const v = data.vendor;
  p.heading('Fourth parties', 'Supply chain');
  p.paragraph(`Fourth parties are the companies ${v.name} itself relies on: hosting, email, payments, analytics, security and other providers. They are found from ${v.domain}'s public footprint (DNS records, certificates, web pages and scripts) or added manually by your team. A weakness at one of these providers can reach ${v.name}, and through it, you. This section is informational and does not change the security rating.`);
  if (!rows.length) {
    p.paragraph(`No fourth parties have been recorded for ${v.name} yet. They are detected after each scan; providers that leave no public trace can be added from the vendor's Fourth Parties tab.`, { color: P.muted });
    return;
  }
  const byCompany = new Map<string, typeof rows>();
  rows.forEach(r => {
    const key = (r.domain || r.company || '').toLowerCase();
    byCompany.set(key, [...(byCompany.get(key) || []), r]);
  });
  const companies = Array.from(byCompany.entries()).map(([key, items]) => {
    const tally = new Map<string, number>();
    items.forEach(i => tally.set(i.category || 'Other', (tally.get(i.category || 'Other') || 0) + 1));
    const category = Array.from(tally.entries()).sort((a, b) => b[1] - a[1])[0][0];
    const manual = items.every(i => i.source === 'manual');
    const how = manual ? 'Added manually by your team' : (items.find(i => i.detected_by)?.detected_by || 'Detected from public footprint');
    return {
      key, company: items[0].company || items[0].domain, domain: items[0].domain, category,
      products: Array.from(new Set(items.map(i => i.product).filter(Boolean))), how,
    };
  }).sort((a, b) => a.category.localeCompare(b.category) || a.company.localeCompare(b.company));
  const categories = Array.from(new Set(companies.map(c => c.category)));
  p.stats([
    { label: 'Companies', value: String(companies.length) },
    { label: 'Products & services', value: String(rows.length) },
    { label: 'Categories', value: String(categories.length) },
  ]);
  p.sub('Fourth parties by company');
  const logos = data.fourth_party_logos || {};
  p.table(['Company', 'Category', 'Products & services', 'How it was found'],
    companies.map(c => [`${c.company}\n${c.domain || ''}`, c.category, c.products.join(', ') || '-', clean(c.how).slice(0, 220)]),
    {
      widths: { 0: 50, 1: 34, 2: 46 }, size: 7.3,
      logoCol: 0,
      logos: companies.map(c => ({ uri: logos[(c.domain || '').toLowerCase()] || null, letter: (c.company || '?').trim().charAt(0).toUpperCase(), key: c.key })),
      colorCol: 1, colors: companies.map(c => hexRgb(categoryColor(c.category))),
    });
}

function evidenceUsed(p: Doc, data: VendorReportData) {
  p.heading('Evidence used to generate this report');
  p.sub('Automated scanning');
  p.paragraph(`This report includes analysis performed on the following domains and IPs as of ${fmtDate(data.scan?.completed_at, true)}.`);
  const hosts = data.hosts || [];
  const active = hosts.filter(h => h.active);
  p.stats([
    { label: 'Active domains & IPs', value: String(active.length + (data.ip_total || 0)) },
    { label: 'Inactive domains & IPs', value: String(hosts.length - active.length) },
    { label: 'Total domains & IPs scanned', value: String(hosts.length + (data.ip_total || 0)) },
  ]);
  p.sub('Domains and IPs monitored');
  p.paragraph('Only active domains and IPs are shown.', { size: 7.6, color: P.muted });
  p.table(['Domain', 'No. subdomains', 'No. IP addresses'],
    [[data.vendor.domain, String(active.filter(h => h.host.toLowerCase() !== data.vendor.domain.toLowerCase()).length), String(data.ip_total || 0)]],
    { widths: { 1: 34, 2: 34 }, align: { 1: 'center', 2: 'center' }, bold0: true });
  if (active.length) {
    p.sub('Hosts assessed');
    p.table(['Host', 'Address', 'Environment'], active.map(h => [h.host, h.ip || '-', h.environment ? h.environment[0].toUpperCase() + h.environment.slice(1) : '-']),
      { widths: { 1: 40, 2: 36 }, size: 7.4 });
  }
}

export function buildVendorReport(data: VendorReportData, meta: ReportMeta, opts: VendorReportOptions): jsPDF {
  const v = data.vendor, s = data.scan;
  const summary = opts.variant === 'summary';
  const title = summary ? `Vendor Summary Report for ${v.domain}` : `Security report for ${v.domain}`;
  const subtitle = `${v.name} (${v.domain})  |  ${fmtDate(meta.generatedAt)}`;
  const p = new Doc(meta, title, subtitle, !summary);
  const subject = v.domain;

  if (!summary) {
    p.cover(['Security report for', v.domain],
      `This report provides a detailed overview of key factors of ${v.domain}'s overall security posture.`,
      [['Generated date', fmtDate(meta.generatedAt)], ['Report for', v.name], ['Issued by', meta.preparedFor], ['Report ID', meta.reportId]]);
    p.newPage();
  }

  if (!s) {
    p.heading('No assessment available');
    p.paragraph(`${v.name} has not completed a security scan yet, so there is nothing to report. Run a scan from the vendor page and generate the report again.`);
    p.finish();
    return p.doc;
  }

  if (summary) {
    assessmentSummary(p, data, subject, false);
    p.newPage(); riskCountTable(p, data);
    p.newPage(); remediationSummary(p, data);
    if (data.fourth_parties?.length) { p.newPage(); fourthPartiesSection(p, data); }
    p.newPage(); evidenceUsed(p, data);
    p.finish();
    return p.doc;
  }

  let first = true;
  const page = () => { if (!first) p.newPage(); first = false; };

  if (opts.introduction) {
    page();
    p.heading('Introduction');
    p.paragraph(`FluxWatch continuously monitors the security posture of ${v.domain} using open-source, commercial and proprietary threat intelligence feeds. Our analysis is centred on objective, externally verifiable information.`);
    p.paragraph(`This report provides rich context for each identified risk, enabling you to make better decisions faster. Risks are grouped into ${data.categories.length} categories: ${data.categories.join(', ')}. Each identified risk is given a severity, a name, a category and the number of assets affected. By default, findings are sorted by severity, with the most critical items at the top.`);
    p.paragraph(`The results outlined below are based on our assessment on ${fmtDate(s.completed_at, true)} and are intended to provide an overview of ${v.domain}'s security posture at that time.`);
    p.y += 3;
    p.sub('Severity levels');
    p.table(['Severity', 'What it means', 'Fix within'], SEVERITIES.map(sv => [sv, SEVERITY_MEANING[sv], PRIORITY[sv].window]),
      { widths: { 0: 28, 2: 32 }, sevIconCol: 0, sevIcons: [...SEVERITIES], bold0: true });

    p.newPage();
    p.heading('How are FluxWatch\'s security ratings calculated?');
    p.paragraph('FluxWatch\'s agentic risk platform continuously assesses and rates the security posture of every organisation it monitors. Threat signals gathered from trusted commercial, open-source and proprietary sources are grouped together to identify threats and issues in an attack surface that could result in a security incident. Signals we pay attention to include:');
    p.bullets([
      'Website and server security headers, cookies and exposed admin interfaces',
      'TLS configuration, certificate validity, key strength and HTTPS enforcement',
      'Email security: SPF, DKIM, DMARC, mail-server encryption and spoofing protection',
      'DNS health: DNSSEC, CAA, name server diversity and dangling records',
      'Open ports, exposed services and known vulnerabilities on public IP addresses',
      'Data breaches, leaked credentials and infostealer exposure',
      'Malware, phishing and blocklist reputation of domains and IPs',
      'Patching cadence and end-of-life software',
      'Legal, regulatory, sanctions and reputational signals',
    ]);
    p.paragraph('Each of the more than 250 checks that finds a problem is given a severity. Each risk category starts at 100 and loses points for its findings, weighted by severity, and categories with more serious findings count for more in the overall rating from 0 to 100, where 100 is best.');
    p.y += 3;
    p.sub('Rating bands');
    p.table(['Grade', 'Rating', 'Interpretation'],
      [['95', '90 - 100', 'Strong security posture'], ['85', '80 - 89', 'Reasonable controls, some gaps'], ['75', '70 - 79', 'Notable gaps in security controls'], ['65', '60 - 69', 'Significant gaps and exposure'], ['30', 'Below 60', 'Serious weaknesses needing urgent attention']],
      { widths: { 0: 18, 1: 30 }, gradeCol: 0, gradeOnly: true });
  }

  if (opts.profile) {
    page();
    p.heading('Company profile');
    const rows: [string, string][] = [
      ['Name', v.name], ['Primary domain', v.domain], ['Industry', v.category || '-'],
      ['Services provided', (v.business_units || []).join(', ') || '-'], ['Location', v.location || '-'],
      ['Parent company', v.parent_company || '-'],
      ['Data sensitivity', v.is_self ? 'Own organisation' : SENSITIVITY[v.data_sensitivity || ''] || 'Not classified'],
      ['Lifecycle stage', LIFECYCLE[v.lifecycle || ''] || '-'], ['Relationship owner', v.owner_name || '-'],
      ['Business owner', v.business_owner || '-'], ['Vendor since', v.vendor_since ? fmtDate(v.vendor_since) : '-'],
      ['Contract end', v.contract_end_date ? fmtDate(v.contract_end_date) : '-'],
    ];
    const rowH = 12, h = Math.ceil(rows.length / 2) * rowH + 6;
    p.card(ML, p.y, CW, h);
    rows.forEach(([k, val], i) => {
      const c = i % 2, r = Math.floor(i / 2);
      const x = ML + 6 + c * (CW / 2), y = p.y + 8.5 + r * rowH;
      p.font('normal', 6.8); p.color(P.muted); p.text(k, x, y);
      p.font('bold', 8.6); p.color(P.ink); p.text(p.wrap(val, CW / 2 - 12)[0] || '-', x, y + 4.8);
    });
    p.y += h + 8;
  }

  page(); assessmentSummary(p, data, subject, true);
  p.newPage(); riskCountTable(p, data);
  if (opts.remediation) { p.newPage(); remediationSummary(p, data); }

  if (opts.categories) {
    const counts = categoryCounts(data);
    const allCats = [...data.categories];
    [...data.findings, ...(data.passing || [])].forEach(i => { if (i.category && !allCats.includes(i.category)) allCats.push(i.category); });
    allCats.forEach(cat => {
      const rows = data.findings.filter(f => f.category === cat);
      const others = (data.passing || []).filter(i => i.category === cat)
        .sort((a, b) => (a.status === 'not_tested' ? 1 : 0) - (b.status === 'not_tested' ? 1 : 0) || (a.name || '').localeCompare(b.name || ''));
      if (!rows.length && !opts.emptyCategories) return;
      if (!counts[cat]) counts[cat] = { Critical: 0, High: 0, Medium: 0, Low: 0 };
      p.newPage();
      p.heading(cat, 'Risk category breakdown');
      // Description on the left, current risks by severity on the right.
      const descW = CW * 0.58, yTop = p.y;
      p.paragraph(CATEGORY_INFO[cat] || '', { w: descW, size: 8.2 });
      const bx = ML + descW + 6, bw = CW - descW - 6, bh = 18;
      p.fill(P.band); p.doc.roundedRect(bx, yTop - 4, bw, bh, 1.5, 1.5, 'F');
      p.label('Current risks by severity', bx + 3.5, yTop + 0.6, P.muted, 5.6);
      SEVERITIES.forEach((sv, i) => {
        const x = bx + 3.5 + i * ((bw - 7) / 4);
        p.font('normal', 5.8); p.color(P.body); p.text(`${sv} risk`, x, yTop + 5.6);
        p.sevIcon(x + 1.7, yTop + 10, sv, 1.7);
        p.font('bold', 7.5); p.color(P.ink); p.text(String(counts[cat][sv] || 0), x + 4.6, yTop + 11.1);
      });
      p.y = Math.max(p.y, yTop + bh) + 4;

      // Category rating with its own 12-month history.
      const sc = s.category_scores?.[cat] ?? null;
      const hist = data.history.map(h => ({ date: h.date, score: h.category_scores?.[cat] as number })).filter(h => typeof h.score === 'number');
      if (data.categories.includes(cat)) {
        const ch = 60;
        p.need(ch + 6);
        const y0 = p.y;
        p.card(ML, y0, CW, ch);
        p.label('Category rating', ML + 5, y0 + 6.5);
        p.bigScore(ML + 5, y0 + 15, sc, 15);
        p.chart(hist, ML + 14, y0 + 21, CW - 22, ch - 31);
        p.y = y0 + ch + 9;
      } else {
        // Company Profile / Funding & Ownership: informational, never part of the rating -- no rating card to show.
        p.paragraph('This category is informational and is not part of the security rating.', { color: P.muted, size: 8 });
      }

      p.sub(`${cat} risk breakdown`);
      // Every indicator in the category: risks first (by severity), then checks that passed, then ones that could not be tested.
      p.table(['Risk', 'Affected', 'Overview', 'Recommendation'],
        [
          ...rows.map(f => {
            const n = f.endpoint_total || 1;
            return [f.name, `${n} asset${n === 1 ? '' : 's'}`, clean(f.description).slice(0, 900), clean(f.recommendation).slice(0, 700) || '-'];
          }),
          ...others.map(i => [i.name, i.status === 'not_tested' ? 'Not tested' : 'Passed', clean(i.description || '').slice(0, 900) || '-',
            i.status === 'not_tested' ? '-' : 'No action needed']),
        ],
        { widths: { 0: 40, 1: 16, 2: 64, 3: 58 }, sevIconCol: 0, size: 7.2, empty: 'No indicators were assessed in this category.',
          sevIcons: [...rows.map(f => f.severity), ...others.map(i => (i.status === 'not_tested' ? 'Not tested' : 'Passed'))] });
    });
  }

  if (opts.geolocation && data.geolocation?.length) {
    p.newPage();
    p.heading('Geolocation');
    p.paragraph('This geographical overview shows the locations that an organisation\'s infrastructure is operating in. Monitoring geolocation risk helps you understand whether data is being hosted in countries that may have different data and privacy laws protecting it.');
    p.y += 2;
    p.stats([{ label: 'Hosting countries', value: String(data.geolocation.length) }, { label: 'IP addresses', value: String(data.ip_total) }]);
    p.table(['Country', 'No. IP addresses', 'Percentage', 'Hosting providers'],
      data.geolocation.map(g => [g.country, String(g.ips), `${Math.round(100 * g.ips / Math.max(1, data.ip_total))}%`, g.owners.join(', ') || '-']),
      { widths: { 0: 44, 1: 28, 2: 24 }, align: { 1: 'center', 2: 'center' }, bold0: true });
  }

  // `!== false`: options saved with an export before this section existed have no key and still get it.
  if (opts.fourthParties !== false) { p.newPage(); fourthPartiesSection(p, data); }

  if (opts.evidenceUsed) { p.newPage(); evidenceUsed(p, data); }

  p.finish();
  return p.doc;
}

/* ─── Portfolio Risk Report ─────────────────────────────────────────────────────────────────────── */
export function buildPortfolioReport(data: PortfolioReportData, meta: ReportMeta, opts: PortfolioReportOptions): jsPDF {
  const company = data.company_name || meta.preparedFor;
  const p = new Doc(meta, `Portfolio risk report for ${company}`, `${data.vendors.length} vendors  |  ${fmtDate(meta.generatedAt)}`, true);
  const all = data.vendors;
  const scored = all.filter(v => v.score != null) as (PortfolioVendor & { score: number })[];
  const avg = scored.length ? Math.round(scored.reduce((a, v) => a + v.score, 0) / scored.length) : null;
  const elevated = scored.filter(v => ['high', 'critical'].includes((v.risk_level || '').toLowerCase()));
  const stale = all.filter(v => (daysSince(v.scan_date) ?? 9999) > 30);
  const totals = all.reduce((t, v) => {
    SEVERITIES.forEach(s => { t[s] = (t[s] || 0) + (v.counts?.[s.toLowerCase()] || 0); });
    return t;
  }, {} as Record<string, number>);
  const ranked = [...all].sort((a, b) => (a.score ?? -1) - (b.score ?? -1));

  p.cover(['Portfolio risk report for', company],
    `This report provides an overview of the security posture of the ${all.length} vendor${all.length === 1 ? '' : 's'} ${company} monitors.`,
    [['Generated date', fmtDate(meta.generatedAt)], ['Vendors', String(all.length)], ['Issued by', meta.preparedFor], ['Report ID', meta.reportId]]);
  p.newPage();

  p.heading('Assessment summary');
  p.ratingCard(avg, 'On average, the portfolio', 'Average vendor rating');
  p.stats([
    { label: 'Vendors', value: String(all.length) },
    { label: 'Assessed', value: String(scored.length) },
    { label: 'High / critical risk', value: String(elevated.length) },
    { label: 'Stale (>30 days)', value: String(stale.length) },
  ]);
  p.ratingBars(ranked.filter(v => v.score != null).slice(0, 18).map(v => ({ cat: v.name, score: v.score })), 'Vendor ratings', 'Lowest rating first');
  p.sub('Open risks across the portfolio');
  p.table(['Severity', 'Open risks', 'Vendors affected'], SEVERITIES.map(sv => [sv, String(totals[sv] || 0), String(all.filter(v => (v.counts?.[sv.toLowerCase()] || 0) > 0).length)]),
    { widths: { 0: 40 }, align: { 1: 'center', 2: 'center' }, sevIconCol: 0, sevIcons: [...SEVERITIES], bold0: true });
  const obs: string[] = [];
  if (elevated.length) obs.push(`Vendors needing escalation: ${elevated.slice(0, 5).map(v => `${v.name} (${v.score})`).join(', ')}.`);
  const movers = scored.filter(v => v.previous_score != null && v.score - (v.previous_score as number) <= -5);
  if (movers.length) obs.push(`Rating dropped by 5 or more since the previous scan: ${movers.map(v => `${v.name} (${v.previous_score} to ${v.score})`).join(', ')}.`);
  const catAvg = data.categories.map(cat => {
    const vals = scored.map(v => v.category_scores?.[cat]).filter((x): x is number => typeof x === 'number');
    return { cat, avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
  }).filter(x => x.avg != null) as { cat: string; avg: number }[];
  const weak = [...catAvg].sort((a, b) => a.avg - b.avg).slice(0, 3).filter(x => x.avg < 95);
  if (weak.length) obs.push(`Weakest categories on average: ${weak.map(x => `${x.cat} (${Math.round(x.avg)})`).join(', ')}.`);
  const never = all.filter(v => v.score == null);
  if (never.length) obs.push(`${never.length} vendor${never.length === 1 ? ' has' : 's have'} never been assessed: ${never.slice(0, 5).map(v => v.name).join(', ')}.`);
  if (stale.length) obs.push(`${stale.length} vendor assessment${stale.length === 1 ? ' is' : 's are'} older than 30 days.`);
  if (obs.length) { p.sub('Key observations'); p.bullets(obs); }

  p.newPage();
  p.heading('Vendor ranking');
  p.table(['Vendor', 'Rating', 'Change', 'Critical', 'High', 'Medium', 'Low', 'Last assessed'],
    ranked.map(v => {
      const d = v.score != null && v.previous_score != null ? v.score - v.previous_score : null;
      return [`${v.name}\n${v.domain}`, v.score == null ? '-' : String(v.score), d == null ? '-' : d === 0 ? '0' : `${d > 0 ? '+' : ''}${d}`,
        String(v.counts?.critical || '-'), String(v.counts?.high || '-'), String(v.counts?.medium || '-'), String(v.counts?.low || '-'),
        v.scan_date ? fmtDate(v.scan_date) : 'Never'];
    }),
    { widths: { 1: 22, 2: 15, 3: 15, 4: 13, 5: 15, 6: 13, 7: 24 }, align: { 2: 'center', 3: 'center', 4: 'center', 5: 'center', 6: 'center' }, gradeCol: 1, bold0: true });

  if (opts.heatmap) {
    p.newPage();
    p.heading('Category heatmap', 'Risk category breakdown');
    p.paragraph('Each cell is the vendor\'s rating in that risk category from its latest assessment. A dash means the category was not assessed.', { color: P.muted });
    const nameW = 42, cellW = (CW - nameW) / data.categories.length, rowH = 7;
    const header = () => {
      p.need(14);
      data.categories.forEach((cat, i) => {
        p.font('bold', 5.6); p.color(P.muted);
        p.doc.text((SHORT_CAT[cat] || cat).toUpperCase(), ML + nameW + i * cellW + cellW / 2, p.y, { align: 'center' });
      });
      p.y += 3;
    };
    header();
    ranked.forEach(v => {
      if (p.y + rowH > BOTTOM) { p.newPage(); header(); }
      p.font('bold', 7.2); p.color(P.ink); p.text(p.wrap(v.name, nameW - 3)[0] || v.name, ML, p.y + 4.7);
      data.categories.forEach((cat, i) => {
        const val = v.category_scores?.[cat];
        const band = HEAT[gradeOf(typeof val === 'number' ? val : null)];
        const x = ML + nameW + i * cellW;
        p.fill(band.bg); p.doc.rect(x + 0.35, p.y + 0.35, cellW - 0.7, rowH - 0.7, 'F');
        p.font('bold', 6.4); p.color(band.fg);
        p.doc.text(typeof val === 'number' ? String(Math.round(val)) : '-', x + cellW / 2, p.y + 4.6, { align: 'center' });
      });
      p.y += rowH;
    });
    p.y += 8;
    if (catAvg.length) p.ratingBars([...catAvg].sort((a, b) => a.avg - b.avg).map(x => ({ cat: x.cat, score: x.avg })), 'Portfolio average by category', 'Lowest average first');
  }

  if (opts.topRisks) {
    p.newPage();
    p.heading('Top risks across the portfolio');
    const rows = all.flatMap(v => (v.top_findings || []).map(f => ({ v, f })))
      .sort((a, b) => (SEVERITIES.indexOf(a.f.severity as any) - SEVERITIES.indexOf(b.f.severity as any)) || (a.f.score_impact - b.f.score_impact))
      .slice(0, 60);
    p.table(['Risk', 'Vendor', 'Category', 'Fix within'],
      rows.map(({ v, f }) => [f.name, v.name, f.category, PRIORITY[f.severity]?.window || '-']),
      { widths: { 1: 40, 2: 38, 3: 28 }, sevIconCol: 0, sevIcons: rows.map(r => r.f.severity), empty: 'No critical or high-severity risks are open at any vendor.' });
    const recurring: Record<string, Set<string>> = {};
    rows.forEach(({ v, f }) => (recurring[f.name] = recurring[f.name] || new Set()).add(v.name));
    const common = Object.entries(recurring).filter(([, s]) => s.size > 1).sort((a, b) => b[1].size - a[1].size).slice(0, 8);
    if (common.length) {
      p.sub('Recurring weaknesses');
      p.bullets(common.map(([name, s]) => `${name}: ${s.size} vendors (${Array.from(s).slice(0, 4).join(', ')}${s.size > 4 ? ', ...' : ''}).`));
    }
  }

  if (opts.calendar) {
    p.newPage();
    p.heading('Assessment and renewal calendar');
    p.sub('Assessments older than 30 days');
    p.table(['Vendor', 'Domain', 'Last assessed', 'Age', 'Rating'],
      [...stale].sort((a, b) => (daysSince(b.scan_date) ?? 99999) - (daysSince(a.scan_date) ?? 99999)).map(v => [v.name, v.domain, v.scan_date ? fmtDate(v.scan_date) : 'Never', v.scan_date ? `${daysSince(v.scan_date)} days` : '-', v.score == null ? '-' : String(v.score)]),
      { widths: { 2: 28, 3: 20, 4: 22 }, gradeCol: 4, bold0: true, empty: 'Every vendor has been assessed in the last 30 days.' });
    const soon = all.filter(v => v.contract_end_date && Math.ceil((new Date(v.contract_end_date).getTime() - Date.now()) / 86_400_000) <= 180)
      .sort((a, b) => (a.contract_end_date || '').localeCompare(b.contract_end_date || ''));
    p.sub('Contracts ending within 180 days');
    p.table(['Vendor', 'Contract end', 'Days left', 'Rating', 'Lifecycle'],
      soon.map(v => {
        const days = Math.ceil((new Date(v.contract_end_date!).getTime() - Date.now()) / 86_400_000);
        return [v.name, fmtDate(v.contract_end_date), days < 0 ? `Ended ${-days} days ago` : String(days), v.score == null ? '-' : String(v.score), LIFECYCLE[v.lifecycle || ''] || '-'];
      }),
      { widths: { 1: 28, 2: 30, 3: 22, 4: 32 }, gradeCol: 3, bold0: true, empty: 'No contracts end in the next 180 days.' });
  }

  if (opts.methodology) {
    p.newPage();
    p.heading('How are FluxWatch\'s security ratings calculated?');
    p.paragraph('Each vendor is continuously assessed by FluxWatch\'s agentic risk platform from externally observable evidence: website and email security, encryption, DNS, exposed services, vulnerabilities, breaches, reputation and legal signals. More than 250 checks are scored across 14 risk categories and combined into a rating from 0 to 100, where 100 is best.');
    p.paragraph('This report is based on passive, non-intrusive observation at the time of each vendor\'s latest assessment. It does not include internal testing or a review of policies, may not identify every weakness, and does not constitute legal, audit or compliance advice.', { color: P.muted });
  }

  p.finish();
  return p.doc;
}

/* ─── Board Summary Report ──────────────────────────────────────────────────────────────────────── */
export interface BoardReportData extends PortfolioReportData {
  organization: null | {
    name: string; domain: string; scan: VendorReportData['scan'];
    history: { date: string; score: number }[]; findings: ReportFinding[];
  };
  vendor_history: Record<string, { date: string; score: number }[]>;
  remediation: { in_progress: number; awaiting_review: number; completed: number; completed_30d: number; open_risks: number; overdue: number };
}
export interface BoardReportOptions { talkingPoints: boolean; movers: boolean; distribution: boolean }
export const DEFAULT_BOARD_OPTIONS: BoardReportOptions = { talkingPoints: true, movers: true, distribution: true };

const GRADE_BANDS: { g: string; range: string; min: number; max: number }[] = [
  { g: 'A', range: '90 - 100', min: 90, max: 100 }, { g: 'B', range: '80 - 89', min: 80, max: 89.999 },
  { g: 'C', range: '70 - 79', min: 70, max: 79.999 }, { g: 'D', range: '60 - 69', min: 60, max: 69.999 },
  { g: 'F', range: '0 - 59', min: 0, max: 59.999 },
];
// Business impact from the data the vendor handles (same order as services/risk_tiering.py), highest first.
const IMPACT: { key: string; label: string; weight: number }[] = [
  { key: 'regulated', label: 'Regulated', weight: 4 }, { key: 'financial', label: 'Financial', weight: 3 },
  { key: 'pii', label: 'Personal data', weight: 3 }, { key: 'internal', label: 'Internal', weight: 2 },
  { key: 'none', label: 'No sensitive', weight: 1 }, { key: '', label: 'Unclassified', weight: 0 },
];

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function buildBoardReport(data: BoardReportData, meta: ReportMeta, opts: BoardReportOptions): jsPDF {
  const org = data.organization;
  const company = org?.name || data.company_name || meta.preparedFor;
  const p = new Doc(meta, 'Board Summary Report', `${company}${org ? ` (${org.domain})` : ''}  |  ${fmtDate(meta.generatedAt)}`, false);
  const vendors = data.vendors;
  const now = Date.now(), DAY = 86_400_000;

  // A vendor's rating at a point in time = its latest scan on or before then.
  const scoreAt = (id: string, t: number) => {
    let s: number | null = null;
    for (const h of data.vendor_history[id] || []) { if (new Date(h.date).getTime() <= t) s = h.score; else break; }
    return s;
  };
  const monthEnds = Array.from({ length: 12 }, (_, i) => {
    if (i === 11) return new Date(now);
    const d = new Date(now);
    return new Date(d.getFullYear(), d.getMonth() - (10 - i), 0, 23, 59, 59);
  });
  const scored = vendors.filter(v => v.score != null) as (PortfolioVendor & { score: number })[];
  const avg = scored.length ? Math.round(scored.reduce((a, v) => a + v.score, 0) / scored.length) : null;
  const vendorAvgSeries: { date: string; score: number }[] = [];
  monthEnds.forEach(d => {
    const vals = vendors.map(v => scoreAt(v.id, d.getTime())).filter((x): x is number => x != null);
    if (vals.length) vendorAvgSeries.push({ date: d.toISOString(), score: vals.reduce((a, b) => a + b, 0) / vals.length });
  });
  const elevated = scored.filter(v => ['high', 'critical'].includes((v.risk_level || '').toLowerCase()) || v.score < 70);
  const openCritical = vendors.reduce((a, v) => a + (v.counts?.critical || 0), 0);
  const createdAt = (v: PortfolioVendor) => (v as any).created_at as string | undefined;
  const newThisMonth = vendors.filter(v => createdAt(v) && now - new Date(createdAt(v)!).getTime() <= 30 * DAY).length;
  const movers = vendors.map(v => {
    const before = scoreAt(v.id, now - 30 * DAY);
    return { v, before, delta: v.score != null && before != null ? v.score - before : null };
  });

  /* Page 1: headline + your organisation */
  p.heading('Board summary');
  const own = org?.scan?.score ?? null;
  let headline: string;
  if (org?.scan && own != null) {
    const diff = avg == null ? null : own - avg;
    const vs = diff == null ? '' : `, ${diff >= 0 ? `${plural(diff, 'point')} above` : `${plural(-diff, 'point')} below`} the average of the ${plural(scored.length, 'vendor')} it monitors`;
    headline = `${company} is rated ${own} out of 100 (grade ${gradeOf(own)})${vs}. ${elevated.length ? `${plural(elevated.length, 'vendor')} need${elevated.length === 1 ? 's' : ''} attention.` : 'No vendor is rated high risk.'}`;
  } else {
    headline = `${company} monitors ${plural(vendors.length, 'vendor')} with an average rating of ${avg ?? '-'} out of 100. Set up My Organization to add your own rating to this report.`;
  }
  p.paragraph(headline, { size: 10, color: P.ink });
  p.y += 1;
  p.stats([
    { label: 'Your rating', value: own == null ? '-' : `${own} (${gradeOf(own)})` },
    { label: 'Vendor average', value: avg == null ? '-' : `${avg} (${gradeOf(avg)})` },
    { label: 'Need attention', value: String(elevated.length) },
    { label: 'Open critical risks', value: String(openCritical) },
  ]);

  if (org?.scan) {
    const s = org.scan;
    p.sub("Your organization's security rating");
    const y0 = p.y, h = 104, lw = 62;
    p.card(ML, y0, CW, h);
    p.label('Overall security rating', ML + 5, y0 + 7);
    p.bigScore(ML + 5, y0 + 17, s.score, 18);
    p.font('normal', 7.6); p.color(P.body);
    p.doc.text(p.wrap(`${company} ${RATING_SENTENCE[gradeOf(s.score)]}`, lw - 10), ML + 5, y0 + 25);
    // Current risks by severity, in a pale box.
    const by = y0 + h - 27;
    p.fill(P.band); p.doc.roundedRect(ML + 4, by, lw - 8, 22, 1.5, 1.5, 'F');
    p.label('Current risks by severity', ML + 7, by + 5.5, P.muted, 5.4);
    SEVERITIES.forEach((sv, i) => {
      const x = ML + 7 + i * ((lw - 14) / 4);
      p.font('normal', 5.6); p.color(P.body); p.text(sv, x, by + 11);
      p.sevIcon(x + 1.6, by + 15.6, sv, 1.6);
      p.font('bold', 7.4); p.color(P.ink); p.text(String(s.counts?.[sv.toLowerCase()] || 0), x + 4.3, by + 16.7);
    });
    // Category bars on the right.
    p.stroke(P.line); p.doc.setLineWidth(0.25); p.doc.line(ML + lw, y0 + 4, ML + lw, y0 + h - 4);
    const rows = data.categories.map(cat => ({ cat, score: s.category_scores?.[cat] ?? null })).sort((a, b) => (a.score ?? 101) - (b.score ?? 101));
    const bx0 = ML + lw + 5, labelW = 30, bx = bx0 + labelW + 11, bw = ML + CW - 5 - bx, rowH = (h - 18) / rows.length;
    p.font('bold', 7.6); p.color(P.ink); p.text('Rating by category', bx0, y0 + 7);
    p.font('normal', 5.4); p.color(P.faint);
    for (let t = 0; t <= 100; t += 20) {
      const x = bx + bw * t / 100;
      p.doc.text(String(t), x, y0 + 12, { align: 'center' });
      p.stroke(P.grid); p.doc.setLineWidth(0.15); p.doc.line(x, y0 + 13.5, x, y0 + h - 4);
    }
    rows.forEach((r, i) => {
      const cy = y0 + 17 + i * rowH + rowH / 2 - 1;
      p.font('normal', 6.2); p.color(P.ink); p.text(p.wrap(r.cat, labelW)[0], bx0, cy + 0.9);
      p.grade(bx0 + labelW + 2.2, cy, r.score, 1.7);
      p.font('normal', 6); p.color(P.body); p.text(r.score == null ? '-' : String(Math.round(r.score)), bx0 + labelW + 4.8, cy + 0.9);
      if (r.score) { p.fill(gradeColor(r.score)); p.doc.rect(bx, cy - 1.7, Math.max(0.8, bw * Math.min(r.score, 100) / 100), 3.4, 'F'); }
    });
    p.y = y0 + h + 8;

    p.sub('Security rating, last 12 months');
    const cy0 = p.y, ch = 62;
    p.card(ML, cy0, CW, ch);
    // Legend: your rating (solid) vs vendor average (dashed).
    p.stroke(P.chart); p.doc.setLineWidth(0.6); p.doc.line(ML + CW - 78, cy0 + 6.5, ML + CW - 72, cy0 + 6.5);
    p.font('normal', 6.6); p.color(P.body); p.text('Your rating', ML + CW - 70.5, cy0 + 7.3);
    p.stroke(P.faint); p.doc.setLineDashPattern([1.2, 1], 0); p.doc.line(ML + CW - 44, cy0 + 6.5, ML + CW - 38, cy0 + 6.5); p.doc.setLineDashPattern([], 0);
    p.text('Vendor average', ML + CW - 36.5, cy0 + 7.3);
    p.chart(org.history, ML + 14, cy0 + 12, CW - 22, ch - 23, [{ points: vendorAvgSeries, color: P.faint }]);
    p.y = cy0 + ch + 8;
  } else {
    p.need(26);
    p.card(ML, p.y, CW, 20);
    p.font('bold', 9); p.color(P.ink); p.text('Your organization is not set up yet', ML + 6, p.y + 8.5);
    p.font('normal', 8); p.color(P.muted); p.text('Add your company domain under My Organization to include your own rating, categories and history.', ML + 6, p.y + 14);
    p.y += 28;
  }

  /* Page 2: vendor risk overview */
  p.newPage();
  p.heading('Vendor risk overview', 'Third-party portfolio');
  {
    const y0 = p.y, h = 82, mw = CW - 52;
    p.card(ML, y0, CW, h);
    p.font('bold', 8); p.color(P.ink); p.text('Vendor rating by business impact', ML + 5, y0 + 7);
    p.font('normal', 6.4); p.color(P.muted); p.text('Business impact comes from the data each vendor handles.', ML + 5, y0 + 11.5);
    const gx = ML + 27, gw = (mw - 27) / IMPACT.length, gy = y0 + 20, rh = 10.5;
    IMPACT.forEach((im, i) => {
      p.font('bold', 5.4); p.color(P.muted);
      p.doc.text(im.label.toUpperCase(), gx + i * gw + gw / 2, gy - 2, { align: 'center' });
    });
    GRADE_BANDS.forEach((b, r) => {
      const y = gy + r * rh;
      p.grade(ML + 8, y + rh / 2, (b.min + b.max) / 2, 1.9);
      p.font('normal', 6.4); p.color(P.body); p.text(b.range, ML + 11.5, y + rh / 2 + 1);
      IMPACT.forEach((im, i) => {
        const n = scored.filter(v => (v.data_sensitivity || '') === im.key && v.score >= b.min && v.score <= b.max).length;
        const x = gx + i * gw;
        let bg: RGB = [247, 249, 252], fg: RGB = P.faint;
        if (n) {
          const level = r + (im.weight ? im.weight : 1.5);  // how bad the grade is + how sensitive the data is
          const c = level >= 6 ? HEAT.F : level >= 4.5 ? HEAT.D : level >= 3 ? HEAT.C : HEAT.A;
          bg = c.bg; fg = c.fg;
        }
        p.fill(bg); p.doc.roundedRect(x + 0.6, y + 0.6, gw - 1.2, rh - 1.2, 1, 1, 'F');
        p.font(n ? 'bold' : 'normal', n ? 8 : 6.5); p.color(fg);
        p.doc.text(n ? String(n) : '-', x + gw / 2, y + rh / 2 + 1.2, { align: 'center' });
      });
    });
    p.font('normal', 5.6); p.color(P.faint);
    p.doc.text('HIGHER IMPACT', gx, gy + 5 * rh + 4);
    p.doc.text('LOWER IMPACT', gx + IMPACT.length * gw, gy + 5 * rh + 4, { align: 'right' });
    // Side panel.
    const sx = ML + mw + 3, sw = CW - mw - 3;
    p.fill(P.band); p.doc.rect(sx, y0 + 0.3, sw - 0.3, h - 0.6, 'F');
    p.label('Vendor average rating', sx + 5, y0 + 10, P.muted, 5.6);
    p.bigScore(sx + 5, y0 + 20, avg, 14);
    p.label('Monitored vendors', sx + 5, y0 + 33, P.muted, 5.6);
    p.font('bold', 14); p.color(P.ink); p.text(String(vendors.length), sx + 5, y0 + 41);
    p.font('normal', 6.6); p.color(newThisMonth ? GRADE.A : P.muted); p.text(`${newThisMonth} added in 30 days`, sx + 5, y0 + 46);
    p.label('Not assessed', sx + 5, y0 + 58, P.muted, 5.6);
    p.font('bold', 14); p.color(P.ink); p.text(String(vendors.length - scored.length), sx + 5, y0 + 66);
    p.y = y0 + h + 9;
  }

  if (opts.distribution) {
    p.sub('Distribution of vendor ratings (monthly)');
    const y0 = p.y, h = 74, cw = CW - 46;
    p.card(ML, y0, CW, h);
    const counts = monthEnds.map(d => {
      const c: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, F: 0 };
      vendors.forEach(v => { const s = scoreAt(v.id, d.getTime()); if (s != null) c[gradeOf(s)]++; });
      return c;
    });
    const max = Math.max(1, ...counts.map(c => Object.values(c).reduce((a, b) => a + b, 0)));
    const x0 = ML + 12, top = y0 + 8, w = cw - 16, hh = h - 22, bw = w / 12;
    p.font('normal', 5.6); p.color(P.faint);
    p.doc.text('VENDORS', ML + 4, top - 2);
    for (let t = 0; t <= max; t += Math.max(1, Math.ceil(max / 4))) {
      const y = top + hh - (t / max) * hh;
      p.stroke(P.grid); p.doc.setLineWidth(0.15); p.doc.line(x0, y, x0 + w, y);
      p.font('normal', 5.6); p.color(P.faint); p.doc.text(String(t), x0 - 2.5, y + 1, { align: 'right' });
    }
    counts.forEach((c, i) => {
      let y = top + hh;
      (['F', 'D', 'C', 'B', 'A'] as const).forEach(g => {
        if (!c[g]) return;
        const bh = (c[g] / max) * hh;
        p.fill(GRADE[g]); p.doc.rect(x0 + i * bw + bw * 0.18, y - bh, bw * 0.64, bh, 'F');
        y -= bh;
      });
      p.font('normal', 5.4); p.color(P.faint);
      p.doc.text(monthEnds[i].toLocaleDateString('en-US', { month: 'short' }), x0 + i * bw + bw / 2, top + hh + 4.5, { align: 'center' });
    });
    // Current ratings legend.
    const lx = ML + cw + 2;
    p.fill(P.band); p.doc.rect(lx, y0 + 0.3, CW - cw - 2.3, h - 0.6, 'F');
    p.label('Current ratings', lx + 4, y0 + 8, P.muted, 5.6);
    GRADE_BANDS.forEach((b, i) => {
      const y = y0 + 16 + i * 11;
      p.grade(lx + 6, y, (b.min + b.max) / 2, 1.9);
      p.font('normal', 6.6); p.color(P.ink); p.text(b.range, lx + 9.5, y + 0.9);
      const n = scored.filter(v => v.score >= b.min && v.score <= b.max).length;
      p.font('normal', 6.2); p.color(P.muted); p.text(plural(n, 'vendor'), lx + 9.5, y + 4.6);
    });
    p.y = y0 + h + 8;
  }

  /* Page 3: rating change + talking points */
  if (opts.movers) {
    p.newPage();
    p.heading('Vendor rating change', 'Last 30 days');
    const rows = movers.filter(m => m.delta != null).sort((a, b) => (a.delta as number) - (b.delta as number));
    const y0 = p.y, rowH = 7, h = Math.max(30, 16 + rows.length * rowH);
    p.card(ML, y0, CW, h);
    if (!rows.length) {
      p.font('normal', 8); p.color(P.muted); p.text('No vendor has two assessments 30 days apart yet.', ML + CW / 2, y0 + h / 2, { align: 'center' });
    } else {
      const maxAbs = Math.max(5, ...rows.map(r => Math.abs(r.delta as number)));
      const nameW = 48, cx = ML + nameW + (CW - nameW - 10) / 2, half = (CW - nameW - 10) / 2 - 22;
      p.stroke(P.line); p.doc.setLineWidth(0.3); p.doc.line(cx, y0 + 8, cx, y0 + h - 4);
      p.font('normal', 5.6); p.color(P.faint);
      p.doc.text('DECLINED', cx - 3, y0 + 6, { align: 'right' }); p.doc.text('IMPROVED', cx + 3, y0 + 6);
      rows.forEach((r, i) => {
        const y = y0 + 13 + i * rowH, d = r.delta as number;
        p.font('bold', 7); p.color(P.ink); p.text(p.wrap(r.v.name, nameW - 6)[0], ML + 5, y + 1);
        const len = d === 0 ? 0.8 : Math.max(0.8, half * Math.abs(d) / maxAbs);
        p.fill(d < 0 ? GRADE.F : d > 0 ? GRADE.A : P.faint);
        p.doc.rect(d < 0 ? cx - len : cx, y - 1.8, len, 3.6, 'F');
        p.font('bold', 6.8); p.color(d < 0 ? GRADE.F : d > 0 ? GRADE.A : P.muted);
        const label = `${d > 0 ? '+' : ''}${d}  (${r.before} to ${r.v.score})`;
        if (d < 0) p.doc.text(label, cx - len - 2, y + 1, { align: 'right' });
        else p.doc.text(label, cx + len + 2, y + 1);
      });
    }
    p.y = y0 + h + 10;

    const ranked = movers.filter(m => m.v.score != null);
    const decline = ranked.filter(m => (m.delta ?? 0) < 0).sort((a, b) => (a.delta as number) - (b.delta as number)).slice(0, 5);
    const improve = ranked.filter(m => (m.delta ?? 0) > 0).sort((a, b) => (b.delta as number) - (a.delta as number)).slice(0, 5);
    const colW = (CW - 6) / 2;
    const table = (title: string, list: typeof ranked, x: number, yStart: number, empty: string) => {
      p.font('bold', 9.5); p.color(P.ink); p.text(title, x, yStart);
      const h = 10 + Math.max(1, list.length) * 8;
      p.card(x, yStart + 3, colW, h);
      p.font('normal', 5.8); p.color(P.muted);
      p.text('Vendor', x + 9, yStart + 9); p.text('Rating', x + colW - 30, yStart + 9); p.text('Change', x + colW - 13, yStart + 9);
      if (!list.length) { p.font('normal', 7.2); p.color(P.muted); p.text(empty, x + 9, yStart + 16); }
      list.forEach((m, i) => {
        const y = yStart + 16 + i * 8;
        p.font('normal', 7); p.color(P.muted); p.text(String(i + 1), x + 4, y);
        p.font('bold', 7.4); p.color(P.ink); p.text(p.wrap(m.v.name, colW - 46)[0], x + 9, y);
        p.grade(x + colW - 28, y - 1, m.v.score, 1.8);
        p.font('normal', 7.2); p.color(P.body); p.text(String(m.v.score), x + colW - 25, y);
        const d = m.delta;
        p.font('bold', 7.2); p.color(d == null || d === 0 ? P.muted : d < 0 ? GRADE.F : GRADE.A);
        p.text(d == null ? 'new' : `${d > 0 ? '+' : ''}${d}`, x + colW - 13, y);
      });
      return yStart + 3 + h;
    };
    p.need(70);
    const ys = p.y;
    const e1 = table('Greatest decline', decline, ML, ys, 'No vendor rating declined.');
    const e2 = table('Greatest improvement', improve, ML + colW + 6, ys, 'No vendor rating improved.');
    p.y = Math.max(e1, e2) + 10;
  }

  if (opts.talkingPoints) {
    if (!opts.movers) p.newPage();
    p.need(70);
    p.heading('Talking points for the board');
    const r = data.remediation;
    p.stats([
      { label: 'Open remediation', value: String(r.in_progress + r.awaiting_review) },
      { label: 'Risks being fixed', value: String(r.open_risks) },
      { label: 'Overdue requests', value: String(r.overdue) },
      { label: 'Closed in 30 days', value: String(r.completed_30d) },
    ]);
    const points: string[] = [];
    if (org?.scan) {
      const crit = org.scan.counts?.critical || 0;
      points.push(`Our own rating is ${own} (${gradeOf(own)}).${crit
        ? ` ${plural(crit, 'critical finding')} on our own estate should be closed first: ${org.findings.filter(f => f.severity === 'Critical').slice(0, 3).map(f => f.name).join(', ')}.`
        : ' There are no critical findings on our own estate.'}`);
    }
    points.push(`We monitor ${plural(vendors.length, 'vendor')} with an average rating of ${avg ?? '-'}${elevated.length
      ? `; ${elevated.map(v => `${v.name} (${v.score})`).slice(0, 4).join(', ')} need${elevated.length === 1 ? 's' : ''} attention`
      : ', and none is rated high risk'}.`);
    const withCrit = vendors.filter(v => (v.counts?.critical || 0) > 0);
    if (withCrit.length) points.push(`${plural(openCritical, 'critical risk')} ${openCritical === 1 ? 'is' : 'are'} open across ${plural(withCrit.length, 'vendor')}: ${withCrit.slice(0, 4).map(v => `${v.name} (${v.counts.critical})`).join(', ')}.`);
    const worst = movers.filter(m => (m.delta ?? 0) <= -5).sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0))[0];
    if (worst) points.push(`${worst.v.name}'s rating fell by ${plural(Math.abs(worst.delta as number), 'point')} in the last 30 days, the largest decline in the portfolio.`);
    const daysTo = (d?: string | null) => (d ? (new Date(d).getTime() - now) / DAY : null);
    const renewals = vendors.filter(v => { const t = daysTo(v.contract_end_date); return t != null && t >= 0 && t <= 90; });
    if (renewals.length) points.push(`${plural(renewals.length, 'contract')} ${renewals.length === 1 ? 'renews' : 'renew'} in the next 90 days: ${renewals.slice(0, 4).map(v => `${v.name} (${fmtDate(v.contract_end_date)}, rated ${v.score ?? '-'})`).join(', ')}.`);
    if (r.overdue) points.push(`${plural(r.overdue, 'remediation request')} ${r.overdue === 1 ? 'is' : 'are'} past due.`);
    p.sub('Where we stand');
    p.bullets(points);

    const actions: string[] = [];
    if (withCrit.length) actions.push(`Escalate the critical findings at ${withCrit.slice(0, 3).map(v => v.name).join(', ')} and agree remediation dates.`);
    const stale = vendors.filter(v => (daysSince(v.scan_date) ?? 9999) > 30);
    if (stale.length) actions.push(`Reassess ${plural(stale.length, 'vendor')} whose last assessment is older than 30 days.`);
    const unclassified = vendors.filter(v => !v.data_sensitivity);
    if (unclassified.length) actions.push(`Classify the data handled by ${plural(unclassified.length, 'vendor')} so their business impact can be weighed.`);
    if (renewals.some(v => (v.score ?? 100) < 80)) actions.push('Make security improvements a condition of renewal for vendors rated below 80.');
    if (org?.scan && (org.scan.counts?.critical || org.scan.counts?.high)) actions.push('Fund and track the fixes for our own critical and high findings this quarter.');
    if (!actions.length) actions.push('Keep the current monitoring cadence; no escalations are needed this period.');
    p.sub('Recommended actions');
    p.bullets(actions);
    p.paragraph("All figures come from FluxWatch's continuous, externally observed assessments at the dates shown.", { size: 7.4, color: P.faint });
  }

  p.finish();
  return p.doc;
}


/* ─── Pre Onboarding summary ────────────────────────────────────────────────────────────────────── */
// The due-diligence record of one vendor request: what was asked for, what the screening scan and
// public sources showed, and what was decided. Exported from the Pre Onboarding review drawer only.

export interface PreOnboardingReportData {
  request: {
    id: string; vendor_name: string; domain: string; reason: string | null; data_sensitivity: string;
    estimated_spend: string | null; risk_tier: string; status: string; reviewer_notes: string | null;
    decided_at: string | null; created_at: string; requested_by_name: string | null;
    contact_name: string | null; contact_email: string | null; contact_phone: string | null;
    ai_research_summary: string | null; ai_research_sources: { label: string; url: string | null }[] | null;
    ai_suggested_data_sensitivity: string | null; ai_research_rationale: string | null;
    insight_summary: string | null;
    insight_top_findings: { name: string; category: string | null; severity: string; score_gain_if_fixed?: number }[] | null;
    insight_generated_at: string | null;
  };
  reviewer_name: string | null;
  scan: {
    score: number | null; risk_level: string | null; completed_at: string | null;
    severity_counts: Record<string, number>; category_scores: Record<string, number>; indicator_count: number;
  } | null;
  documents: { title: string; source_url: string; document_type: string | null; found_at: string | null }[];
  timeline: { user_name: string | null; action: string; details: string | null; created_at: string }[];
}

const REQUEST_STATUS: Record<string, string> = {
  pending: 'Pending review', needs_info: 'Needs more information', approved: 'Approved',
  rejected: 'Rejected', promoted: 'Onboarded',
};
const REQUEST_ACTION: Record<string, string> = {
  'vendor_request.scanned': 'Screening scan started',
  'vendor_request.decided': 'Decision recorded',
  'vendor_request.sensitivity_updated': 'Data sensitivity changed',
  'vendor_request.public_documents_found': 'Public documents found',
  'vendor_request.summary_exported': 'Summary exported',
  'vendor_request.deleted': 'Request deleted',
};

export function buildPreOnboardingReport(data: PreOnboardingReportData, meta: ReportMeta): jsPDF {
  const r = data.request, s = data.scan;
  const title = `Pre-onboarding summary for ${r.domain}`;
  const subtitle = `${r.vendor_name} (${r.domain})  |  ${fmtDate(meta.generatedAt)}`;
  const p = new Doc(meta, title, subtitle, true);
  const status = REQUEST_STATUS[r.status] || r.status;

  p.cover(['Pre-onboarding summary for', r.domain],
    `The due-diligence record for ${r.vendor_name}: why it was requested, what screening and public sources showed, and the decision taken.`,
    [['Generated date', fmtDate(meta.generatedAt)], ['Vendor', r.vendor_name], ['Status', status], ['Report ID', meta.reportId]]);

  // Request overview
  p.newPage();
  p.heading('Request overview');
  p.stats([
    { label: 'Inherent tier', value: r.risk_tier || '-' },
    { label: 'Screening score', value: s?.score != null ? `${Math.round(s.score)} / 100` : 'Not screened' },
    { label: 'Status', value: status },
  ]);
  p.table(['Field', 'Value'], [
    ['Vendor', `${r.vendor_name} (${r.domain})`],
    ['Requested by', r.requested_by_name || '-'],
    ['Requested on', fmtDate(r.created_at, true)],
    ['Contact', r.contact_name || '-'],
    ['Contact email', r.contact_email || '-'],
    ['Contact phone', r.contact_phone || '-'],
    ['Data it will handle', SENSITIVITY[r.data_sensitivity] || r.data_sensitivity || '-'],
    ['Estimated spend', r.estimated_spend || '-'],
  ], { widths: { 0: 48 }, bold0: true });
  if (r.reason) { p.sub('Why it is needed'); p.paragraph(r.reason); }

  // Decision
  p.sub('Decision');
  if (r.status === 'pending') {
    p.paragraph('No decision has been recorded yet; this request is still waiting for review.', { color: P.muted });
  } else {
    p.table(['Field', 'Value'], [
      ['Outcome', status],
      ['Decided by', data.reviewer_name || '-'],
      ['Decided on', fmtDate(r.decided_at, true)],
      ['Reviewer notes', r.reviewer_notes || '-'],
    ], { widths: { 0: 48 }, bold0: true });
  }

  // Screening scan
  p.newPage();
  p.heading('Screening results', 'Security screening');
  if (!s) {
    p.paragraph(`No screening scan was completed for ${r.domain} before this summary was generated.`, { color: P.muted });
  } else {
    p.paragraph(`A screening scan checked ${r.domain} against ${s.indicator_count} security checks on ${fmtDate(s.completed_at, true)}. It looks only at what is visible from the outside.`);
    p.ratingCard(s.score, r.domain, 'Screening security rating');
    p.sub('Open risks by severity');
    p.table(['Severity', 'Open risks', 'Fix within'], SEVERITIES.map(sv => [sv, s.severity_counts?.[sv] ?? 0, PRIORITY[sv].window]),
      { widths: { 0: 40, 1: 30 }, align: { 1: 'center' }, sevIconCol: 0, sevIcons: [...SEVERITIES], bold0: true });
    const cats = Object.entries(s.category_scores || {}).sort((a, b) => a[1] - b[1]);
    if (cats.length) p.ratingBars(cats.map(([cat, score]) => ({ cat, score })), 'Rating by category', 'Lowest-rated categories first');
  }

  // Insight agent
  const findings = r.insight_top_findings || [];
  if (r.insight_summary || findings.length) {
    p.newPage();
    p.heading('Security insight', 'What would move the rating');
    if (r.insight_summary) p.paragraph(r.insight_summary);
    if (findings.length) {
      p.sub('Top open findings');
      p.table(['Severity', 'Finding', 'Category', 'Gain if fixed'],
        findings.map(f => [f.severity, f.name, f.category || '-',
          typeof f.score_gain_if_fixed === 'number' && f.score_gain_if_fixed > 0 ? `+${f.score_gain_if_fixed} pts` : '-']),
        { widths: { 0: 26, 2: 40, 3: 26 }, align: { 3: 'right' }, sevIconCol: 0, sevIcons: findings.map(f => f.severity) });
      p.paragraph('Gains are the FluxWatch scoring formula re-run with that finding fixed on its own.', { size: 7.4, color: P.faint });
    }
  }

  // About the company (research assist)
  if (r.ai_research_summary || r.ai_suggested_data_sensitivity) {
    p.need(40);
    p.y += 4;
    p.sub('About the company');
    if (r.ai_research_summary) p.paragraph(r.ai_research_summary);
    if (r.ai_suggested_data_sensitivity) {
      p.paragraph(`Suggested data sensitivity: ${SENSITIVITY[r.ai_suggested_data_sensitivity] || r.ai_suggested_data_sensitivity}`
        + (r.ai_research_rationale ? `. ${r.ai_research_rationale}` : ''), { color: P.ink });
    }
    const sources = (r.ai_research_sources || []).map(src => src.label).filter(Boolean);
    if (sources.length) p.paragraph(`Sources: ${sources.join(', ')}`, { size: 7.4, color: P.faint });
  }

  // Public documents
  p.need(40);
  p.y += 4;
  p.sub('Public documents');
  p.table(['Document', 'Type', 'Found', 'Source'],
    data.documents.map(d => [d.title, d.document_type || '-', fmtDate(d.found_at), d.source_url]),
    { widths: { 1: 30, 2: 22, 3: 62 }, size: 7, empty: 'No publicly available documents (SEC filings, trust-center reports) were found for this vendor.' });

  // Activity
  p.need(40);
  p.y += 4;
  p.sub('Activity');
  const activity: (string | number)[][] = data.timeline.map(t => [fmtDate(t.created_at), t.user_name || '-', REQUEST_ACTION[t.action] || t.action, t.details || '']);
  activity.push([fmtDate(r.created_at), r.requested_by_name || '-', 'Request created', `${r.vendor_name} (${r.domain})`]);
  p.table(['Date', 'By', 'Action', 'Details'], activity, { widths: { 0: 24, 1: 30, 2: 38 }, size: 7 });

  p.finish();
  return p.doc;
}
